'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function GdpCalculator() {
  const t = useTranslations("tools.gdpCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [currentTab, setCurrentTab] = useState<'expenditure' | 'income'>('expenditure');
  const [showResults, setShowResults] = useState(false);

  // Expenditure approach inputs
  const [expenditureInputs, setExpenditureInputs] = useState({
    personalConsumption: 14000000000000,
    grossInvestment: 3500000000000,
    governmentConsumption: 3000000000000,
    exports: 2500000000000,
    imports: 3000000000000,
  });

  // Income approach inputs
  const [incomeInputs, setIncomeInputs] = useState({
    employeeCompensation: 11000000000000,
    proprietorsIncome: 1500000000000,
    rentalIncome: 700000000000,
    corporateProfits: 2200000000000,
    interestIncome: 600000000000,
    indirectTaxes: 1200000000000,
    depreciation: 2800000000000,
    netForeignIncome: -200000000000,
  });

  const [results, setResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const handleExpenditureChange = (field: string, value: string) => {
    setExpenditureInputs({
      ...expenditureInputs,
      [field]: parseFloat(value) || 0,
    });
  };

  const handleIncomeChange = (field: string, value: string) => {
    setIncomeInputs({
      ...incomeInputs,
      [field]: parseFloat(value) || 0,
    });
  };

  const calculateExpenditureGDP = (e: React.FormEvent) => {
    e.preventDefault();

    const { personalConsumption, grossInvestment, governmentConsumption, exports, imports } = expenditureInputs;
    const netExports = exports - imports;
    const gdp = personalConsumption + grossInvestment + governmentConsumption + netExports;

    setResults({
      type: 'expenditure',
      gdp,
      breakdown: [
        { label: t('personalConsumption'), value: personalConsumption },
        { label: t('grossInvestment'), value: grossInvestment },
        { label: t('governmentConsumption'), value: governmentConsumption },
        { label: t('exports'), value: exports },
        { label: t('imports'), value: imports },
        { label: t('netExports'), value: netExports },
      ],
      formula: `GDP = C + I + G + (X - M)`,
      calculation: `GDP = ${formatCurrency(personalConsumption)} + ${formatCurrency(grossInvestment)} + ${formatCurrency(governmentConsumption)} + ${formatCurrency(netExports)}`,
    });

    setShowResults(true);
  };

  const calculateIncomeGDP = (e: React.FormEvent) => {
    e.preventDefault();

    const {
      employeeCompensation,
      proprietorsIncome,
      rentalIncome,
      corporateProfits,
      interestIncome,
      indirectTaxes,
      depreciation,
      netForeignIncome,
    } = incomeInputs;

    const gnp = employeeCompensation + proprietorsIncome + rentalIncome + corporateProfits + interestIncome;
    const gdp = gnp + indirectTaxes + depreciation + netForeignIncome;

    setResults({
      type: 'income',
      gdp,
      gnp,
      breakdown: [
        { label: t('employeeCompensation'), value: employeeCompensation },
        { label: t('proprietorsIncome'), value: proprietorsIncome },
        { label: t('rentalIncome'), value: rentalIncome },
        { label: t('corporateProfits'), value: corporateProfits },
        { label: t('interestIncome'), value: interestIncome },
        { label: t('gnpSum'), value: gnp },
        { label: t('indirectTaxes'), value: indirectTaxes },
        { label: t('depreciation'), value: depreciation },
        { label: t('netForeignIncome'), value: netForeignIncome },
      ],
      formula: `GNP = Employee Compensation + Proprietors' Income + Rental Income + Corporate Profits + Interest Income`,
      calculation: `GDP = GNP + Indirect Business Taxes + Depreciation + Net Income of Foreigners`,
      calculationDetail: `GDP = ${formatCurrency(gnp)} + ${formatCurrency(indirectTaxes)} + ${formatCurrency(depreciation)} + ${formatCurrency(netForeignIncome)}`,
    });

    setShowResults(true);
  };

  const handleClear = () => {
    if (currentTab === 'expenditure') {
      setExpenditureInputs({
        personalConsumption: 0,
        grossInvestment: 0,
        governmentConsumption: 0,
        exports: 0,
        imports: 0,
      });
    } else {
      setIncomeInputs({
        employeeCompensation: 0,
        proprietorsIncome: 0,
        rentalIncome: 0,
        corporateProfits: 0,
        interestIncome: 0,
        indirectTaxes: 0,
        depreciation: 0,
        netForeignIncome: 0,
      });
    }
    setShowResults(false);
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const tabsContainerStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', marginBottom: '1.5rem', justifyContent: 'center', flexWrap: 'wrap' as const },
    {},
    { marginBottom: '2rem' },
    {}
  );

  const tabButtonStyle = (isActive: boolean) =>
    getResponsiveStyle(
      {
        border: isActive ? 'none' : `2px solid #e2e8f0`,
        background: isActive ? 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)' : 'white',
        color: isActive ? 'white' : '#64748b',
        borderRadius: '0.75rem',
        cursor: 'pointer',
        fontWeight: 600,
        transition: 'all 0.3s ease',
      },
      { padding: '0.625rem 1.25rem', fontSize: '0.875rem' },
      { padding: '0.75rem 1.5rem', fontSize: '1rem' },
      { padding: '0.75rem 1.5rem', fontSize: '1rem' }
    );

  const formStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      marginBottom: '1.5rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const formGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginBottom: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(2, 1fr)' }
  );

  const formGroupStyle = { display: 'flex', flexDirection: 'column' as const };

  const labelStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#334155', marginBottom: '0.5rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      width: '100%',
    },
    { padding: '0.75rem 1rem', fontSize: '1rem' },
    { padding: '0.75rem 1rem', fontSize: '1rem' },
    { padding: '0.75rem 1rem', fontSize: '1rem' }
  );

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '2rem' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      border: 'none',
      borderRadius: '0.75rem',
      fontWeight: 600,
      cursor: 'pointer',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.3)',
      transition: 'all 0.3s ease',
    },
    { padding: '0.875rem 2rem', fontSize: '1rem', width: '100%' },
    { padding: '0.875rem 2rem', fontSize: '1rem', width: 'auto' },
    { padding: '0.875rem 2rem', fontSize: '1rem', width: 'auto' }
  );

  const clearButtonStyle = getResponsiveStyle(
    {
      border: 'none',
      borderRadius: '0.75rem',
      fontWeight: 600,
      cursor: 'pointer',
      background: '#f1f5f9',
      color: '#64748b',
      transition: 'all 0.3s ease',
    },
    { padding: '0.875rem 2rem', fontSize: '1rem', width: '100%' },
    { padding: '0.875rem 2rem', fontSize: '1rem', width: 'auto' },
    { padding: '0.875rem 2rem', fontSize: '1rem', width: 'auto' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      display: showResults ? 'block' : 'none',
      animation: showResults ? 'slideIn 0.4s ease' : 'none',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const resultMainStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      borderRadius: '0.75rem',
      textAlign: 'center' as const,
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const resultMainValueStyle = getResponsiveStyle(
    { fontWeight: 700 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '2.5rem' }
  );

  const resultItemStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem',
    background: '#f8fafc',
    borderRadius: '0.5rem',
    borderLeft: '4px solid #2563eb',
    marginBottom: '1rem',
  };

  const resultLabelStyle = { fontWeight: 600, color: '#334155', flex: 1 };
  const resultValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#2563eb', textAlign: 'right' as const },
    { fontSize: '1rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.25rem' }
  );

  const formulaDisplayStyle = getResponsiveStyle(
    {
      background: '#fef3c7',
      borderLeft: '4px solid #f59e0b',
      borderRadius: '0.5rem',
      marginTop: '1.5rem',
    },
    { padding: '1rem', fontSize: '0.875rem' },
    { padding: '1rem', fontSize: '1rem' },
    { padding: '1rem', fontSize: '1rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.7, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.8, paddingLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem', marginTop: '1.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const referenceStyle = getResponsiveStyle(
    { color: colors.textSecondary, marginBottom: '0.5rem', lineHeight: 1.6 },
    { fontSize: '0.875rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const linkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-all' as const,
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolHeaderStyle}>
          <h2 style={h2Style}>{t('toolTitle')}</h2>
          <p style={summaryTextStyle}>{t('toolSubtitle')}</p>
        </div>

        <div style={tabsContainerStyle}>
          <button
            style={tabButtonStyle(currentTab === 'expenditure')}
            onClick={() => {
              setCurrentTab('expenditure');
              setShowResults(false);
            }}
          >
            {t('expenditureApproach')}
          </button>
          <button
            style={tabButtonStyle(currentTab === 'income')}
            onClick={() => {
              setCurrentTab('income');
              setShowResults(false);
            }}
          >
            {t('incomeApproach')}
          </button>
        </div>

        <form style={formStyle} onSubmit={currentTab === 'expenditure' ? calculateExpenditureGDP : calculateIncomeGDP}>
          {currentTab === 'expenditure' ? (
            <>
              <h3 style={h3Style}>{t('expenditureApproach')}</h3>
              <div style={formGridStyle}>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="personal-consumption">
                    {t('personalConsumptionLabel')}
                  </label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="personal-consumption"
                    step="0.01"
                    value={expenditureInputs.personalConsumption}
                    onChange={(e) => handleExpenditureChange('personalConsumption', e.target.value)}
                    placeholder="e.g., 14000000000000"
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="gross-investment">
                    {t('grossInvestmentLabel')}
                  </label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="gross-investment"
                    step="0.01"
                    value={expenditureInputs.grossInvestment}
                    onChange={(e) => handleExpenditureChange('grossInvestment', e.target.value)}
                    placeholder="e.g., 3500000000000"
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="government-consumption">
                    {t('governmentConsumptionLabel')}
                  </label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="government-consumption"
                    step="0.01"
                    value={expenditureInputs.governmentConsumption}
                    onChange={(e) => handleExpenditureChange('governmentConsumption', e.target.value)}
                    placeholder="e.g., 3000000000000"
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="exports">
                    {t('exportsLabel')}
                  </label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="exports"
                    step="0.01"
                    value={expenditureInputs.exports}
                    onChange={(e) => handleExpenditureChange('exports', e.target.value)}
                    placeholder="e.g., 2500000000000"
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="imports">
                    {t('importsLabel')}
                  </label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="imports"
                    step="0.01"
                    value={expenditureInputs.imports}
                    onChange={(e) => handleExpenditureChange('imports', e.target.value)}
                    placeholder="e.g., 3000000000000"
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              <h3 style={h3Style}>{t('incomeApproach')}</h3>
              <div style={formGridStyle}>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="employee-compensation">
                    {t('employeeCompensationLabel')}
                  </label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="employee-compensation"
                    step="0.01"
                    value={incomeInputs.employeeCompensation}
                    onChange={(e) => handleIncomeChange('employeeCompensation', e.target.value)}
                    placeholder="e.g., 11000000000000"
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="proprietors-income">
                    {t('proprietorsIncomeLabel')}
                  </label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="proprietors-income"
                    step="0.01"
                    value={incomeInputs.proprietorsIncome}
                    onChange={(e) => handleIncomeChange('proprietorsIncome', e.target.value)}
                    placeholder="e.g., 1500000000000"
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="rental-income">
                    {t('rentalIncomeLabel')}
                  </label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="rental-income"
                    step="0.01"
                    value={incomeInputs.rentalIncome}
                    onChange={(e) => handleIncomeChange('rentalIncome', e.target.value)}
                    placeholder="e.g., 700000000000"
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="corporate-profits">
                    {t('corporateProfitsLabel')}
                  </label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="corporate-profits"
                    step="0.01"
                    value={incomeInputs.corporateProfits}
                    onChange={(e) => handleIncomeChange('corporateProfits', e.target.value)}
                    placeholder="e.g., 2200000000000"
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="interest-income">
                    {t('interestIncomeLabel')}
                  </label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="interest-income"
                    step="0.01"
                    value={incomeInputs.interestIncome}
                    onChange={(e) => handleIncomeChange('interestIncome', e.target.value)}
                    placeholder="e.g., 600000000000"
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="indirect-taxes">
                    {t('indirectTaxesLabel')}
                  </label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="indirect-taxes"
                    step="0.01"
                    value={incomeInputs.indirectTaxes}
                    onChange={(e) => handleIncomeChange('indirectTaxes', e.target.value)}
                    placeholder="e.g., 1200000000000"
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="depreciation">
                    {t('depreciationLabel')}
                  </label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="depreciation"
                    step="0.01"
                    value={incomeInputs.depreciation}
                    onChange={(e) => handleIncomeChange('depreciation', e.target.value)}
                    placeholder="e.g., 2800000000000"
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="net-foreign-income">
                    {t('netForeignIncomeLabel')}
                  </label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="net-foreign-income"
                    step="0.01"
                    value={incomeInputs.netForeignIncome}
                    onChange={(e) => handleIncomeChange('netForeignIncome', e.target.value)}
                    placeholder="e.g., -200000000000"
                  />
                </div>
              </div>
            </>
          )}

          <div style={buttonGroupStyle}>
            <button type="submit" style={calculateButtonStyle}>
              {t('calculateButton')}
            </button>
            <button type="button" style={clearButtonStyle} onClick={handleClear}>
              {t('clearButton')}
            </button>
          </div>
        </form>

        {showResults && results && (
          <div style={resultsStyle}>
            <h3 style={{ ...h2Style, textAlign: 'center' }}>{t('resultsTitle')}</h3>
            <div style={resultMainStyle}>
              <div style={{ fontSize: '1rem', opacity: 0.9, marginBottom: '0.5rem' }}>{t('gdpLabel')}</div>
              <div style={resultMainValueStyle}>{formatCurrency(results.gdp)}</div>
            </div>
            <div>
              {results.breakdown.map((item: any, index: number) => (
                <div key={index} style={resultItemStyle}>
                  <span style={resultLabelStyle}>{item.label}</span>
                  <span style={resultValueStyle}>{formatCurrency(item.value)}</span>
                </div>
              ))}
            </div>
            <div style={formulaDisplayStyle}>
              <strong style={{ color: '#92400e' }}>{t('formulaUsed')}</strong>
              <p style={{ margin: '0.5rem 0 0 0', color: '#78350f', fontFamily: "'Courier New', monospace" }}>
                {results.formula}
              </p>
              {results.calculation && (
                <p style={{ margin: '0.5rem 0 0 0', color: '#78350f', fontFamily: "'Courier New', monospace" }}>
                  {results.calculation}
                </p>
              )}
              {results.calculationDetail && (
                <p style={{ margin: '0.5rem 0 0 0', color: '#78350f', fontFamily: "'Courier New', monospace" }}>
                  {results.calculationDetail}
                </p>
              )}
            </div>
          </div>
        )}
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToUseStep1')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToUseStep2')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToUseStep3')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToUseStep4')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToUseStep5')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>
        <p style={paragraphStyle}>{t('insightsParagraph2')}</p>
        <p style={paragraphStyle}>{t('insightsParagraph3')}</p>
        <p style={paragraphStyle}>{t('insightsParagraph4')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={h3Style}>{t('expenditureApproachTitle')}</h3>
        <p style={paragraphStyle}>{t('expenditureApproachDesc')}</p>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.5rem' }}>{t('expenditureComponent1')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('expenditureComponent2')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('expenditureComponent3')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('expenditureComponent4')}</li>
        </ul>

        <h3 style={h3Style}>{t('incomeApproachTitle')}</h3>
        <p style={paragraphStyle}>{t('incomeApproachDesc')}</p>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.5rem' }}>{t('incomeComponent1')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('incomeComponent2')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('incomeComponent3')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('incomeComponent4')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('incomeComponent5')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={h3Style}>{t('faq1Question')}</h3>
          <p style={paragraphStyle}>{t('faq1Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={h3Style}>{t('faq2Question')}</h3>
          <p style={paragraphStyle}>{t('faq2Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={h3Style}>{t('faq3Question')}</h3>
          <p style={paragraphStyle}>{t('faq3Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={h3Style}>{t('faq4Question')}</h3>
          <p style={paragraphStyle}>{t('faq4Answer')}</p>
        </div>
        <div>
          <h3 style={h3Style}>{t('faq5Question')}</h3>
          <p style={paragraphStyle}>{t('faq5Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={{ ...listStyle, paddingLeft: '1.25rem' }}>
          <li style={referenceStyle}>
            <a
              href="https://fastercapital.com/content/Gross-Domestic-Product-Calculator--Startups-and-GDP--Using-Calculators-to-Measure-Economic-Impact.html"
              target="_blank"
              rel="noopener noreferrer"
              style={linkStyle}
            >
              Gross Domestic Product Calculator: Startups and GDP - FasterCapital
            </a>
          </li>
          <li style={referenceStyle}>
            <a
              href="https://www.religareonline.com/blog/how-to-calculate-gdp/"
              target="_blank"
              rel="noopener noreferrer"
              style={linkStyle}
            >
              How to Calculate GDP | Formula & Guide | Religare Online
            </a>
          </li>
          <li style={referenceStyle}>
            <a
              href="https://www.weforum.org/stories/2019/05/an-economist-explains-how-to-value-the-internet/"
              target="_blank"
              rel="noopener noreferrer"
              style={linkStyle}
            >
              {"Economists have got GDP calculations wrong for years - here's why"}
            </a>
          </li>
          <li style={referenceStyle}>
            <a
              href="https://www.omnicalculator.com/finance/gdp"
              target="_blank"
              rel="noopener noreferrer"
              style={linkStyle}
            >
              GDP Calculator (Gross Domestic Product)
            </a>
          </li>
          <li style={referenceStyle}>
            <a href="https://www.stelorder.com/en/blog/gdp/" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              How is GDP calculated? - STEL Order
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}